import React from "react";
import { Contract } from "@/types";
import ContractStatusEnum from "@/constants/enums/ContractStatusEnum";
import CTA from "@/components/atoms/CTA";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useModal } from "@/contexts/ModalContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { archiveContract } from "@/services/Contracts";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

const ArchiveContractButton = ({ contract }: { contract: Contract }) => {
  const { isViewer, currentCompany } = useAuth();
  const { t } = useTranslation();
  const { openModal } = useModal();
  const queryClient = useQueryClient();

  const isArchived =
    contract.contractStatus === ContractStatusEnum.ARCHIVED || contract.contractStatus === ContractStatusEnum.COMPLETED;

  const { mutate: archiveContractMutation } = useMutation({
    mutationFn: async () => {
      await archiveContract(contract.id);
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:contracts.contractedArchived"))
      );
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:contracts.contractedArchivedError"))
      );
    },
  });

  const handleToggleOfArchive = () => {
    openModal({
      title: t("notifications:contracts.confirmContractArchiveTitle"),
      content: "", //t("notifications:contracts.confirmContractArchiveContent"),
      type: "confirmation",
      primaryAction: {
        onClick: async () => {
          archiveContractMutation();
          await queryClient.invalidateQueries({
            queryKey: ["getContractById", contract.id],
          });
        },
        text: t("actions:archiveContract"),
        intent: "affirm",
      },
      secondaryAction: {
        onClick: () => {
          // just close it
        },
        text: t("actions:cancel"),
        intent: "negate",
      },
    });
  };

  return (
    <CTA
      size="sm"
      data-testid="archive-contract-btn"
      disabled={isArchived || isViewer(currentCompany)}
      onClick={() => handleToggleOfArchive()}
      intent="secondary"
      tt="uppercase"
    >
      {t("actions:archiveContract")}
    </CTA>
  );
};

export default ArchiveContractButton;
